/**
 * Default Tools Structured Editor Component.
 *
 * Replaces raw JSON textarea with visual card-based tool editor.
 * Each tool is displayed as an expandable card with name, description,
 * and input_schema fields. Provides a toggle to switch to raw JSON.
 *
 * Sprint: 44, Task: S44-D05
 */

import { useState, useCallback, useEffect } from 'react';
import { useI18n } from '@/i18n';
import { Input, Label, Textarea, Button } from '@/components/ui';
import {
  Code,
  FormInput,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

interface ToolDef {
  name: string;
  description: string;
  input_schema?: Record<string, unknown>;
}

interface DefaultToolsEditorProps {
  /** JSON string value from react-hook-form */
  value: string;
  /** Called with updated JSON string */
  onChange: (value: string) => void;
  /** Validation error message */
  error?: string;
}

// =============================================================================
// Helpers
// =============================================================================

function parseToolsSafe(str: string): ToolDef[] {
  try {
    const parsed = JSON.parse(str || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function formatJson(tools: ToolDef[]): string {
  return JSON.stringify(tools, null, 2);
}

// =============================================================================
// Tool Card Sub-component
// =============================================================================

function ToolCard({
  tool,
  isExpanded,
  onToggle,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  t,
}: {
  tool: ToolDef;
  isExpanded: boolean;
  onToggle: () => void;
  onUpdate: (field: keyof ToolDef, value: unknown) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  t: (key: string) => string;
}) {
  const [schemaStr, setSchemaStr] = useState(
    tool.input_schema ? JSON.stringify(tool.input_schema, null, 2) : '{}'
  );
  const [schemaError, setSchemaError] = useState(false);

  const handleSchemaChange = (raw: string) => {
    setSchemaStr(raw);
    try {
      const parsed = JSON.parse(raw);
      setSchemaError(false);
      onUpdate('input_schema', parsed);
    } catch {
      setSchemaError(true);
    }
  };

  return (
    <div className="border rounded-lg bg-background">
      {/* Header */}
      <div className="flex items-center gap-2 p-3">
        <div className="flex flex-col gap-0.5">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowUp className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowDown className="w-3 h-3" />
          </button>
        </div>

        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-2 flex-1 text-left"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
          <span className="font-medium text-sm">
            {tool.name || t('admin.systemTemplates.form.toolEditor.untitledTool')}
          </span>
          {!isExpanded && tool.description && (
            <span className="text-xs text-muted-foreground truncate max-w-xs">
              — {tool.description}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={onRemove}
          className="text-muted-foreground hover:text-destructive transition-colors p-1"
          title={t('common.delete')}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-3 border-t pt-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('admin.systemTemplates.form.toolEditor.toolName')} *</Label>
              <Input
                value={tool.name}
                onChange={(e) => onUpdate('name', e.target.value)}
                placeholder="e.g. create_incident"
                className="text-sm"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('admin.systemTemplates.form.toolEditor.toolDescription')} *</Label>
            <Textarea
              value={tool.description}
              onChange={(e) => onUpdate('description', e.target.value)}
              rows={2}
              placeholder="Describe what this tool does..."
              className="text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('admin.systemTemplates.form.toolEditor.inputSchema')}</Label>
            <p className="text-xs text-muted-foreground">
              {t('admin.systemTemplates.form.toolEditor.inputSchemaHelp')}
            </p>
            <Textarea
              value={schemaStr}
              onChange={(e) => handleSchemaChange(e.target.value)}
              rows={6}
              className="font-mono text-xs"
              placeholder="{}"
            />
            {schemaError && (
              <p className="text-xs text-destructive">
                {t('admin.systemTemplates.form.invalidJson')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function DefaultToolsEditor({ value, onChange, error }: DefaultToolsEditorProps) {
  const { t } = useI18n();
  const [isJsonMode, setIsJsonMode] = useState(false);
  const [jsonValue, setJsonValue] = useState(value);
  const [tools, setTools] = useState<ToolDef[]>(() => parseToolsSafe(value));
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set());

  // Sync from parent when value changes externally
  useEffect(() => {
    const parsed = parseToolsSafe(value);
    setTools(parsed);
    setJsonValue(value);
  }, [value]);

  const emitChange = useCallback(
    (updatedTools: ToolDef[]) => {
      setTools(updatedTools);
      const json = formatJson(updatedTools);
      setJsonValue(json);
      onChange(json);
    },
    [onChange]
  );

  const addTool = () => {
    const newIndex = tools.length;
    const updated = [...tools, { name: '', description: '', input_schema: {} }];
    emitChange(updated);
    setExpandedIndices((prev) => new Set(prev).add(newIndex));
  };

  const removeTool = (index: number) => {
    emitChange(tools.filter((_, i) => i !== index));
    setExpandedIndices((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
  };

  const updateTool = (index: number, field: keyof ToolDef, val: unknown) => {
    const updated = tools.map((t, i) => (i === index ? { ...t, [field]: val } : t));
    emitChange(updated);
  };

  const moveTool = (from: number, direction: -1 | 1) => {
    const to = from + direction;
    if (to < 0 || to >= tools.length) return;
    const updated = [...tools];
    [updated[from], updated[to]] = [updated[to], updated[from]];
    emitChange(updated);
    // Update expanded indices
    setExpandedIndices((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => {
        if (i === from) next.add(to);
        else if (i === to) next.add(from);
        else next.add(i);
      });
      return next;
    });
  };

  const toggleExpanded = (index: number) => {
    setExpandedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleJsonChange = useCallback(
    (raw: string) => {
      setJsonValue(raw);
      onChange(raw);
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setTools(parsed);
        }
      } catch {
        // Keep raw value, structured will use last valid
      }
    },
    [onChange]
  );

  const toggleMode = () => {
    if (isJsonMode) {
      setTools(parseToolsSafe(jsonValue));
    } else {
      setJsonValue(formatJson(tools));
    }
    setIsJsonMode(!isJsonMode);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label>{t('admin.systemTemplates.form.defaultTools')}</Label>
          <p className="text-xs text-muted-foreground">{t('admin.systemTemplates.form.defaultToolsHelp')}</p>
        </div>
        <button
          type="button"
          onClick={toggleMode}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded border"
        >
          {isJsonMode ? (
            <>
              <FormInput className="w-3.5 h-3.5" />
              {t('admin.systemTemplates.form.structuredView')}
            </>
          ) : (
            <>
              <Code className="w-3.5 h-3.5" />
              {t('admin.systemTemplates.form.jsonView')}
            </>
          )}
        </button>
      </div>

      {isJsonMode ? (
        <div className="space-y-1.5">
          <Textarea
            value={jsonValue}
            onChange={(e) => handleJsonChange(e.target.value)}
            rows={14}
            className="font-mono text-xs"
            placeholder="[]"
          />
          {error && (
            <p className="text-xs text-destructive">{t('admin.systemTemplates.form.invalidJson')}</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {tools.length === 0 ? (
            <div className="text-center py-6 text-sm text-muted-foreground border rounded-lg border-dashed">
              {t('admin.systemTemplates.form.toolEditor.noTools')}
            </div>
          ) : (
            tools.map((tool, index) => (
              <ToolCard
                key={index}
                tool={tool}
                isExpanded={expandedIndices.has(index)}
                onToggle={() => toggleExpanded(index)}
                onUpdate={(field, val) => updateTool(index, field, val)}
                onRemove={() => removeTool(index)}
                onMoveUp={() => moveTool(index, -1)}
                onMoveDown={() => moveTool(index, 1)}
                isFirst={index === 0}
                isLast={index === tools.length - 1}
                t={t}
              />
            ))
          )}

          <Button type="button" variant="outline" size="sm" onClick={addTool} className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            {t('admin.systemTemplates.form.toolEditor.addTool')}
          </Button>

          {error && (
            <p className="text-xs text-destructive">{t('admin.systemTemplates.form.invalidJson')}</p>
          )}
        </div>
      )}
    </div>
  );
}
